import { useQuery } from '@tanstack/react-query';
import { type FormEvent } from 'react';
import { api, call } from '../../api/client';
import { useAction } from '../../api/queries';
import { useSession } from '../../api/session';
import { Badge, Button, Card, ErrorText, Field, Input, PageHeader } from '../../components/ui';
import { timeAgo } from '../../lib/format';
import { signInErrorText, useSignInOptions } from '../auth/SignInOptions';

/** Google / Microsoft accounts connected to this FamilyChores account (ADR-0021). */
function SignInMethods() {
  const options = useSignInOptions().data;
  const identities = useQuery({ queryKey: ['me', 'identities'], queryFn: () => call(api.GET('/api/v1/auth/identities')) });
  const params = new URLSearchParams(window.location.search);
  const connect = useAction(async (provider: 'google' | 'microsoft') => {
    const { authorizeUrl } = await call(api.POST('/api/v1/auth/oauth/{provider}/link', { params: { path: { provider } }, body: { client: 'WEB' } }));
    window.location.assign(authorizeUrl);
  });
  const disconnect = useAction((identityId: string) => call(api.DELETE('/api/v1/auth/identities/{identityId}', { params: { path: { identityId } } })), {
    success: 'Disconnected',
  });
  const linked = (provider: string) => identities.data?.some((i) => i.provider === provider);
  const label = (p: string) => (p === 'GOOGLE' ? 'Google' : 'Microsoft');
  return (
    <Card>
      <h2 className="mb-3 font-medium">Sign-in methods</h2>
      {params.get('linked') ? <p className="mb-3 text-sm text-[var(--accent)]">Connected. You can now sign in with {params.get('linked') === 'google' ? 'Google' : 'Microsoft'}.</p> : null}
      {params.get('error') ? <p role="alert" className="mb-3 text-sm text-amber-700 dark:text-amber-300">{signInErrorText(params.get('error') ?? undefined)}</p> : null}
      <ul className="space-y-2 text-sm">
        {identities.data?.map((i) => (
          <li key={i.id} className="flex items-center gap-2">
            <span className="flex-1">{label(i.provider)} · {i.email}</span>
            <Button size="sm" variant="ghost" busy={disconnect.isPending} onClick={() => disconnect.mutate(i.id)}>Disconnect</Button>
          </li>
        ))}
        {identities.data && !identities.data.length ? <li className="text-[var(--muted)]">No Google or Microsoft account connected yet.</li> : null}
      </ul>
      <div className="mt-3 flex flex-wrap gap-2">
        {options?.google && !linked('GOOGLE') ? <Button size="sm" variant="secondary" busy={connect.isPending} onClick={() => connect.mutate('google')}>Connect Google</Button> : null}
        {options?.microsoft && !linked('MICROSOFT') ? <Button size="sm" variant="secondary" busy={connect.isPending} onClick={() => connect.mutate('microsoft')}>Connect Microsoft</Button> : null}
      </div>
    </Card>
  );
}

export function AccountPage() {
  const { me, logout } = useSession();
  const passwordEnabled = useSignInOptions().data?.password ?? false;
  const sessions = useQuery({ queryKey: ['me', 'sessions'], queryFn: () => call(api.GET('/api/v1/me/sessions')) });
  const revoke = useAction((sessionId: string) => call(api.DELETE('/api/v1/me/sessions/{sessionId}', { params: { path: { sessionId } } })), { success: 'Signed out that device' });
  const password = useAction(
    (f: FormData) => call(api.POST('/api/v1/me/password', { body: { currentPassword: String(f.get('current')), newPassword: String(f.get('next')) } })),
    { success: 'Password changed. Your other devices were signed out.' },
  );
  const logoutAll = async () => {
    await call(api.POST('/api/v1/auth/logout-all'));
    await logout();
  };
  return (
    <>
      <PageHeader title="Your account" subtitle={me?.email} />
      <div className="space-y-6">
        <Card>
          <h2 className="mb-3 font-medium">Signed-in devices</h2>
          <ul className="space-y-2 text-sm">
            {sessions.data?.map((s) => (
              <li key={s.id} className="flex items-center gap-2">
                <span className="flex-1">{s.deviceLabel ?? s.clientType} · active {timeAgo(s.lastSeenAt)}</span>
                {s.current ? <Badge tone="accent">This device</Badge> : <Button size="sm" variant="ghost" onClick={() => revoke.mutate(s.id)}>Sign out</Button>}
              </li>
            ))}
          </ul>
          <Button className="mt-4" variant="secondary" onClick={() => void logoutAll()}>Sign out everywhere</Button>
        </Card>
        <SignInMethods />
        {passwordEnabled ? (
        <Card>
          <h2 className="mb-3 font-medium">Change password</h2>
          <form className="space-y-3" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); const form = e.currentTarget; password.mutate(new FormData(form), { onSuccess: () => form.reset() }); }}>
            <Field label="Current password">{(id) => <Input id={id} name="current" type="password" required autoComplete="current-password" />}</Field>
            <Field label="New password" hint="At least 12 characters.">{(id) => <Input id={id} name="next" type="password" required minLength={12} autoComplete="new-password" />}</Field>
            <ErrorText error={password.error} />
            <Button type="submit" busy={password.isPending}>Change password</Button>
          </form>
        </Card>
        ) : null}
      </div>
    </>
  );
}
